import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from './ui/button';
import { supabase } from '../lib/supabase';
import { trackProjectCreated, trackFormSubmission, trackButtonClick } from '../lib/posthog-events';
import { getApiUrl } from '@/lib/api-config';
import { useUser } from '@/contexts/UserContext';
import { ProjectForm, type ProjectFormData } from '../features/project/info/ProjectForm';
import { colors } from '@/design-system/colors';

export function NewProjectButton() {
  const { companyId } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseErrorMessage = (error: any): string => {
    if (typeof error === 'string') {
      return error;
    }

    const errorMessage = error?.message || error?.error || '';
    const errorDetails = error?.details || error?.error?.details || '';
    const errorCode = error?.code || error?.error?.code || '';

    if (errorCode === '23505') {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('email') || detailsStr.includes('users_email_unique')) {
        return 'This email address is already in use. Please use a different email.';
      }
      return 'This record already exists. Please check for duplicates.';
    }

    if (errorCode === '23502') {
      const fieldMatch = errorDetails?.match(/column "(\w+)" of relation/);
      if (fieldMatch) {
        const fieldName = fieldMatch[1];
        const friendlyName = fieldName
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (l: string) => l.toUpperCase());
        return `${friendlyName} is required.`;
      }
      return 'A required field is missing. Please check all required fields.';
    }

    if (errorCode === '23503') {
      return 'Invalid reference. Please check that all related records exist.';
    }

    if (errorCode === '23514') {
      if (errorMessage.includes('email')) {
        return 'Please enter a valid email address.';
      }
      if (errorMessage.includes('status') || errorMessage.includes('role')) {
        return 'Invalid status or role value. Please select a valid option.';
      }
      return 'Invalid data format. Please check your input.';
    }

    if (errorMessage.includes('user_id and company_id are required')) {
      return 'Session expired. Please refresh the page and try again.';
    }

    if (errorMessage.includes('Failed to create project') || errorMessage.includes('Failed to update project')) {
      const detailsStr = String(errorDetails || '').toLowerCase();
      if (detailsStr.includes('duplicate key') || detailsStr.includes('unique constraint')) {
        return 'Failed to create project. A project with this information already exists.';
      }
      return 'Failed to create project. Please check all fields and try again.';
    }

    return errorMessage || 'An unexpected error occurred. Please try again.';
  };

  const handleSubmit = async (data: ProjectFormData) => {
    setError(null);
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      if (!companyId) throw new Error('User has no associated company');

      // `attachmentsFiles` (the File objects picked in the form) is client-only: it is uploaded after the project exists (below) and is not a column of `projects`.
      const { attachmentsFiles: _pendingFiles, ...projectFields } = data;
      const projectData = {
        ...projectFields,
        company_id: companyId,
        created_by: session.user.id,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Use generic API for project creation
      const response = await fetch(getApiUrl('api/entities/project'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: session.user.id,
          ...projectData
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const parsedError = parseErrorMessage(errorData);
        throw new Error(parsedError);
      }

      const result = await response.json();
      const createdProject = result.data;

      console.log('Project created:', createdProject);

      // Upload any attachments picked before the project existed -- same
      // "create record, then attach files to the new id" pattern as
      // Create Lead and this same form's own page.tsx handler.
      if (Array.isArray(data.attachmentsFiles) && data.attachmentsFiles.length > 0 && createdProject?.id) {
        try {
          const { uploadProjectAttachments } = await import('../features/project/store');
          await uploadProjectAttachments(createdProject.id, data.attachmentsFiles, companyId, session.user.id);
        } catch (uploadError) {
          console.error('Error uploading attachments after create:', uploadError);
        }
      }

      // Track successful project creation with PostHog
      trackProjectCreated({
        project_id: createdProject.id,
        budget: createdProject.budget,
        project_type: createdProject.project_type,
        project_name: createdProject.name,
        client_name: createdProject.client_name,
        square_footage: createdProject.square_footage,
        status: createdProject.status,
        start_date: createdProject.start_date,
        end_date: createdProject.end_date,
        city: createdProject.city,
        state: createdProject.state,
        company_id: companyId,
        created_by: session.user.id
      });

      // Track successful form submission
      trackFormSubmission({
        form_type: 'project_creation',
        success: true
      });

      setIsOpen(false);
      setError(null);
      
      // Trigger a custom event to refresh projects list in other components
      window.dispatchEvent(new CustomEvent('projectsUpdated'));
    } catch (error: any) {
      console.error('Error creating project:', error);
      
      const userFriendlyError = parseErrorMessage(error);
      
      trackFormSubmission({
        form_type: 'project_creation',
        success: false,
        error_message: userFriendlyError
      });

      setError(userFriendlyError);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setError(null);
  };

  return (
    <>
      <Button 
        id="new-project-btn"
        variant="default" 
        className="text-white" 
        size="sm"
        style={{ backgroundColor: colors.buttonPrimary }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colors.buttonPrimaryHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = colors.buttonPrimary;
        }}
        onClick={() => {
          trackButtonClick('new_project_button', { context: 'header' });
          setIsOpen(true);
        }}
      >
        <Plus className="h-4 w-4 mr-2" />
        New Project
      </Button>

      <ProjectForm
        mode="create"
        initialData={undefined}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isOpen={isOpen}
        error={error}
      />
    </>
  );
} 
